import { useCallback, useEffect, useState } from "react";

import consumer from "utils/consumer";
import { camelCaseKeys, snakeCaseKeys } from "utils/transform-object-keys";

const NOOP = () => {};

type Subscription = ReturnType<typeof consumer.subscriptions.create>;
type Props<T> = {
  channel: string;
  enabled?: boolean;
  key?: string;
  onConnected?: () => void;
  onDisconnected?: () => void;
  onReceived?: (data: T) => void;
};

const useChannel = <T>({
  channel,
  key,
  onConnected = NOOP,
  onDisconnected = NOOP,
  onReceived = NOOP,
  enabled = true,
}: Props<T>) => {
  const [connected, setConnected] = useState(false);
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [pendingActions, setPendingActions] = useState<
    { action: string; data?: unknown }[]
  >([]);

  const perform = useCallback(
    (action: string, rawData?: object) => {
      const data = rawData ? (snakeCaseKeys(rawData) as object) : undefined;

      if (subscription && connected) {
        subscription.perform(action, data);
      } else {
        setPendingActions((prev) => [...prev, { action, data }]);
      }
    },
    [connected, subscription],
  );

  const handleReceived = useCallback(
    (rawData: object) => {
      const data = camelCaseKeys(rawData) as T;
      onReceived(data);
    },
    [onReceived],
  );

  useEffect(() => {
    let newSubscription: Subscription | null = null;

    if (enabled) {
      newSubscription = consumer.subscriptions.create(
        key !== undefined ? { channel, key } : channel,
        {
          received: handleReceived,
          connected: () => {
            setConnected(true);
            onConnected();
          },
          disconnected: () => {
            setConnected(false);
            onDisconnected();
          },
        },
      );
    }

    setSubscription(newSubscription);

    return () => {
      newSubscription?.unsubscribe();
    };
  }, [key, channel, onConnected, onDisconnected, handleReceived, enabled]);

  useEffect(() => {
    if (connected && pendingActions.length > 0) {
      pendingActions.forEach(({ action, data }) => {
        subscription?.perform(action, data as object | undefined);
      });

      setPendingActions([]);
    }
  }, [connected, pendingActions, subscription]);

  return [connected, { perform }] as const;
};

export default useChannel;
